// Horizontal megabar — five grouped dropdowns (Dashboard, Sales ▾,
// Operations ▾, Tools ▾, Settings ▾). Replaces the flat sidebar.
//
// Structure + interaction patterns are ported from the source of truth:
//   /var/www/kitchenos/portal-standalone.html
//     • Markup template: lines 1833–1956
//     • Touch toggle JS:  lines 1288–1354
//     • Dropdown CSS:     lines 506–523
//
// The group→page mapping below lives in the chrome (not in the pages),
// matching how the single-file portal curates its dropdowns. Pages stay
// agnostic to whether they're navigated to from a sidebar or a megabar.

import { renderIcon, type IconName } from "@tkos/shared-ui";
import { navigate, onNavigate, getCurrent, getPage } from "./router";
import { openNewCustomerModal } from "./new-customer-modal";
import type { ToastKind } from "./types";

type ShowToastFn = (msg: string, kind?: ToastKind) => void;

// ── Group + item model ──────────────────────────────────────────────────────

interface MegaItem {
  /** Registered page name, or null if `action` handles the click. */
  page: string | null;
  label: string;
  description?: string;
  /** TKOS icon name (rendered via renderIcon). */
  icon: IconName;
  /** Override the click behaviour (toast, custom navigation, deep-link). */
  action?: (helpers: ItemHelpers) => void;
  /**
   * Permission key from the 18-perm catalogue (see role_permissions.js).
   * The role-gate hides the rendered item button when the current role
   * doesn't have this perm. Omit to keep the item visible to all roles
   * (rare — usually navigation items always carry a perm).
   */
  perm?: string;
}

interface MegaGroup {
  /** Stable id used for the button's data-group attribute. */
  id: string;
  label: string;
  icon: IconName;
  /** If `items` is null, this is a flat link (Dashboard pattern). */
  items: MegaItem[] | null;
  /** Direct destination when `items === null`. */
  directPage?: string;
  /** Subtitle shown above the dropdown's first row. */
  dropTitle?: string;
  /** Optional CTA row at the bottom of the dropdown. */
  cta?: { label: string; icon: IconName; action: (h: ItemHelpers) => void; danger?: boolean };
  /**
   * Permission key OR comma-separated list. When set, role-gate renders
   * the group button only if ANY of these perms is allowed. Use the
   * single-perm form for direct-link groups (Dashboard); use the
   * comma form for grouped dropdowns where the wrapper should hide
   * only once every child is unreachable.
   */
  perm?: string;
}

interface ItemHelpers {
  navigate: typeof navigate;
  toast: ShowToastFn;
  /** Pre-write the settings active-tab key, then navigate to settings. */
  openSettingsTab: (tabId: string) => void;
}

// ── Group definitions (matched 1:1 to source-of-truth megabar) ──────────────

const GROUPS: MegaGroup[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: "dashboard",
    items: null,
    directPage: "dashboard",
    perm: "dashboard",
  },
  {
    id: "sales",
    label: "Sales",
    icon: "chart",
    dropTitle: "Sales & customer pipeline",
    // Group visible if ANY child item is accessible.
    perm: "submissions,crm,messages,bookings,designer-projects",
    items: [
      { page: "submissions", label: "Submissions", description: "New enquiries & hot leads",
        icon: "file", perm: "submissions" },
      { page: "crm", label: "CRM & Comms", description: "Pipeline, contacts, follow-ups",
        icon: "customer", perm: "crm" },
      { page: "messages", label: "Messages", description: "Platform messages",
        icon: "chat", perm: "messages" },
      { page: "bookings", label: "Bookings", description: "Consultations, site visits",
        icon: "calendar", perm: "bookings" },
      { page: "contacts", label: "Contacts", description: "Customers, suppliers & trade phonebook",
        icon: "team", perm: "crm" },
    ],
    cta: {
      label: "New Customer",
      icon: "plus",
      action: (h) => openNewCustomerModal({ showToast: h.toast }),
    },
  },
  {
    id: "operations",
    label: "Operations",
    icon: "kitchen",
    dropTitle: "Project & delivery operations",
    perm: "operations,calendar,delivery,suppliers,financials",
    items: [
      { page: "operations", label: "Operations", description: "Installs, surveys, snags, payments",
        icon: "gear", perm: "operations" },
      { page: "calendar", label: "Calendar", description: "Team events & schedule",
        icon: "calendar", perm: "calendar" },
      { page: "deliveries", label: "Delivery", description: "Retailer orders & inbound",
        icon: "truck", perm: "delivery" },
      { page: "drivers", label: "Manage Drivers", description: "Team, vehicles, logins & jobs",
        icon: "customer", perm: "delivery" },
      { page: "suppliers", label: "Suppliers", description: "Profiles, manuals, contacts",
        icon: "package", perm: "suppliers" },
      { page: "payments", label: "Payments & Billing", description: "Plan, commission, invoices",
        icon: "card", perm: "analytics" },
    ],
    cta: {
      label: "Add calendar event",
      icon: "plus",
      action: (h) => h.navigate("calendar"),
    },
  },
  {
    id: "tools",
    label: "Tools",
    icon: "sparkle",
    dropTitle: "Power tools",
    perm: "suppliers,supplier-pricing,crm,analytics",
    items: [
      { page: "catalogue-manager", label: "Catalogue Manager", description: "Custom categories & CSV uploads",
        icon: "upload", perm: "suppliers" },
      { page: "product-search", label: "Product Search", description: "Browse the full supplier catalogue",
        icon: "search", perm: "suppliers" },
      { page: "quote-builder", label: "Quote Builder", description: "Build customer quotes",
        icon: "file", perm: "crm" },
      { page: "reports", label: "Reports & Analytics", description: "Revenue, conversion, performance",
        icon: "chart", perm: "analytics" },
    ],
    cta: {
      label: "AI Settings",
      icon: "gear",
      action: (h) => h.toast("AI Settings — coming soon", "info"),
    },
  },
  {
    id: "settings",
    label: "Settings",
    icon: "gear",
    dropTitle: "Account & workspace",
    // Settings group always has at least Help + Feedback for every role,
    // so the group itself stays visible to all. Per-item perms still
    // hide the role-restricted sub-items (Team, Integrations, Branding).
    items: [
      { page: null, label: "Team & Permissions", description: "Invite users, manage roles",
        icon: "team", perm: "team-management",
        action: (h) => h.openSettingsTab("team-permissions") },
      { page: null, label: "Integrations", description: "Connect tools & services",
        icon: "link", perm: "settings",
        action: (h) => h.openSettingsTab("integrations") },
      { page: null, label: "Branding", description: "Logo, colours, domain",
        icon: "palette", perm: "settings",
        action: (h) => h.openSettingsTab("branding") },
      { page: null, label: "AI Settings", description: "Configure Kit & assistants",
        icon: "sparkle", perm: "ai-tools",
        action: (h) => h.toast("AI Settings — coming soon", "info") },
      { page: "help", label: "Help & Guides", description: "FAQs, getting started, support",
        icon: "help" },
      { page: "roadmap", label: "Roadmap", description: "What's shipped & coming next",
        icon: "sparkle" },
      { page: "feedback", label: "Feedback & Suggestions", description: "Report bugs, request features",
        icon: "lightbulb" },
    ],
    cta: {
      label: "Sign out",
      icon: "log-out",
      action: () => {
        // Sign-out is initiated from header.ts (it owns the confirm flow);
        // dispatching a CustomEvent lets the header listen without coupling.
        document.dispatchEvent(new CustomEvent("tkos:request-signout"));
      },
      danger: true,
    },
  },
];

// ── Settings tab deep-link helper ───────────────────────────────────────────

const SETTINGS_STORAGE_KEY = "tkos.settings.active_tab";

function openSettingsTab(tabId: string): void {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, tabId);
  } catch { /* ignore quota / private mode */ }
  navigate("settings");
}

// ── Component ───────────────────────────────────────────────────────────────

export interface MegabarOptions {
  showToast: ShowToastFn;
  /** Called whenever an item is activated (so the shell can close mobile drawer). */
  onAfterNavigate?: () => void;
}

export interface MegabarHandle {
  el: HTMLElement;
  destroy: () => void;
}

export function createMegabar(opts: MegabarOptions): MegabarHandle {
  const helpers: ItemHelpers = {
    navigate,
    toast: opts.showToast,
    openSettingsTab,
  };

  const nav = document.createElement("nav");
  nav.className = "tkos-megabar";
  nav.id = "tkos-megabar";
  nav.setAttribute("aria-label", "Primary navigation");

  const inner = document.createElement("div");
  inner.className = "tkos-megabar__inner";
  nav.appendChild(inner);

  // Build each group.
  const groupEls = new Map<string, HTMLDivElement>();

  for (const group of GROUPS) {
    const wrap = document.createElement("div");
    wrap.className = "tkos-mega-item-wrap";
    if (group.items !== null) wrap.classList.add("tkos-mega-group");
    wrap.dataset["group"] = group.id;
    // Role-gate hook: wrapper hides when the calling role has none of the
    // group's perms. Comma-separated list means "any allowed → show".
    // Single key means "this perm required". Groups without `perm` set
    // (e.g. Settings — Help/Feedback are always allowed) stay visible.
    if (group.perm) {
      if (group.perm.includes(",")) {
        wrap.setAttribute("data-perm-any", group.perm);
      } else {
        wrap.setAttribute("data-perm", group.perm);
      }
    }

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tkos-mega-item";
    if (group.items !== null) btn.classList.add("tkos-mega-item-group");
    if (group.directPage) btn.dataset["page"] = group.directPage;
    btn.appendChild(renderIcon(group.icon, { size: "sm" }));
    const labelSpan = document.createElement("span");
    labelSpan.textContent = group.label;
    btn.appendChild(labelSpan);
    if (group.items !== null) {
      const caret = document.createElement("span");
      caret.className = "tkos-mega-car";
      caret.setAttribute("aria-hidden", "true");
      caret.appendChild(renderIcon("chevron-down", { size: "xs" }));
      btn.appendChild(caret);
    }
    btn.addEventListener("click", () => {
      if (group.directPage) {
        navigate(group.directPage);
        opts.onAfterNavigate?.();
      }
      // For group buttons, click is a no-op on desktop (dropdown is :hover);
      // touch handler below intercepts to toggle .open.
    });
    wrap.appendChild(btn);

    // Dropdown.
    if (group.items !== null) {
      const drop = document.createElement("div");
      drop.className = "tkos-mega-drop";

      if (group.dropTitle) {
        const title = document.createElement("div");
        title.className = "tkos-mega-drop__title";
        title.textContent = group.dropTitle;
        drop.appendChild(title);
      }

      for (const item of group.items) {
        const link = document.createElement("a");
        link.className = "tkos-mega-drop__link";
        if (item.page) link.dataset["page"] = item.page;
        if (item.perm) link.setAttribute("data-perm", item.perm);
        link.appendChild(renderIcon(item.icon, { size: "sm" }));
        const text = document.createElement("span");
        const bold = document.createElement("b");
        bold.textContent = item.label;
        text.appendChild(bold);
        if (item.description) {
          const em = document.createElement("em");
          em.textContent = item.description;
          text.appendChild(em);
        }
        link.appendChild(text);
        link.addEventListener("click", (e) => {
          e.preventDefault();
          if (item.action) {
            item.action(helpers);
          } else if (item.page) {
            navigate(item.page);
          }
          // Close any open dropdowns after activation (touch + desktop).
          wrap.classList.remove("is-open");
          opts.onAfterNavigate?.();
        });
        drop.appendChild(link);
      }

      if (group.cta) {
        const divider = document.createElement("div");
        divider.className = "tkos-mega-drop__divider";
        drop.appendChild(divider);

        const ctaLink = document.createElement("a");
        ctaLink.className = "tkos-mega-drop__link tkos-mega-drop__cta";
        if (group.cta.danger) ctaLink.classList.add("is-danger");
        ctaLink.appendChild(renderIcon(group.cta.icon, { size: "sm" }));
        const span = document.createElement("span");
        span.textContent = group.cta.label;
        ctaLink.appendChild(span);
        ctaLink.addEventListener("click", (e) => {
          e.preventDefault();
          group.cta!.action(helpers);
          wrap.classList.remove("is-open");
          opts.onAfterNavigate?.();
        });
        drop.appendChild(ctaLink);
      }

      wrap.appendChild(drop);
    }

    inner.appendChild(wrap);
    groupEls.set(group.id, wrap);
  }

  // ── Active-page tracking ────────────────────────────────────────────────
  // Mark the matching mega-item (or any dropdown link with data-page=<name>)
  // active. For dropdown items, the parent group's button also lights up.

  const setActive = (name: string | null): void => {
    inner.querySelectorAll<HTMLElement>(".tkos-mega-item.is-active").forEach((el) => {
      el.classList.remove("is-active");
    });
    if (!name) return;
    // Direct-page group match (e.g. Dashboard).
    const direct = inner.querySelector<HTMLElement>(`.tkos-mega-item[data-page="${name}"]`);
    if (direct) {
      direct.classList.add("is-active");
      return;
    }
    // Dropdown link match → light up the parent group's button.
    const link = inner.querySelector<HTMLAnchorElement>(`.tkos-mega-drop__link[data-page="${name}"]`);
    if (link) {
      const groupBtn = link.closest(".tkos-mega-item-wrap")?.querySelector<HTMLElement>(".tkos-mega-item");
      groupBtn?.classList.add("is-active");
    }
  };

  setActive(getCurrent());
  const offNav = onNavigate((name) => setActive(name));

  // ── Touch toggle (ported from source-of-truth) ──────────────────────────
  // Two-tap pattern on touch devices: first tap opens the dropdown, second
  // tap navigates. Tap anywhere else closes all open dropdowns. Desktop
  // hover is CSS-driven and doesn't go through this path.

  let isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  const onFirstTouch = (): void => { isTouchDevice = true; };
  document.addEventListener("touchstart", onFirstTouch, { passive: true, once: true });

  const onTouchStart = (e: TouchEvent): void => {
    if (!isTouchDevice) return;
    const target = e.target as Element | null;
    if (!target) return;

    const groupBtn = target.closest(".tkos-mega-item-group") as HTMLElement | null;
    if (groupBtn) {
      const wrap = groupBtn.closest(".tkos-mega-item-wrap") as HTMLElement | null;
      if (!wrap) return;
      if (wrap.classList.contains("is-open")) {
        wrap.classList.remove("is-open");
        return;
      }
      e.preventDefault();
      // Close other open dropdowns.
      inner.querySelectorAll(".tkos-mega-item-wrap.is-open").forEach((w) => {
        if (w !== wrap) w.classList.remove("is-open");
      });
      wrap.classList.add("is-open");
      return;
    }
    // Tap on a dropdown link → let the click handler run; close all wraps.
    const link = target.closest(".tkos-mega-drop__link");
    if (link) {
      inner.querySelectorAll(".tkos-mega-item-wrap.is-open").forEach((w) => w.classList.remove("is-open"));
      return;
    }
    // Tap outside → close all.
    inner.querySelectorAll(".tkos-mega-item-wrap.is-open").forEach((w) => w.classList.remove("is-open"));
  };
  document.addEventListener("touchstart", onTouchStart, { passive: false });

  // Quietly warn (dev console only) about megabar items whose target page
  // isn't registered yet. Useful during the staged rebuild.
  if (import.meta.env.DEV) {
    for (const group of GROUPS) {
      if (group.directPage && !getPage(group.directPage)) {
        console.warn(`[megabar] direct page "${group.directPage}" not registered`);
      }
      for (const item of group.items ?? []) {
        if (item.page && !getPage(item.page)) {
          console.warn(`[megabar] page "${item.page}" referenced by "${item.label}" is not registered`);
        }
      }
    }
  }

  return {
    el: nav,
    destroy: () => {
      offNav();
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchstart", onFirstTouch);
    },
  };
}
